import type {
  ActivityReceipt,
  Agent,
  AgentEnrollment,
  ContextPack,
  ContextPackRequest,
  GraphPayload,
  Note,
  Overview,
  PackPreview,
  Proposal,
  PublicAgentEnrollment,
  SmartContextResult,
  BulkTakeoutAcceptance,
  TakeoutCandidate,
  TakeoutCandidatePage,
  TakeoutImport,
  TakeoutReviewPage,
  TaxonomyItem,
  Mission,
  LivingSkill,
  MemoryAutomation,
  DecisionRadar,
  MemoryChanges,
  Dossier,
  DossierSummary,
  OutcomeSummary,
  ReleaseFeedbackRating,
  ReleaseFeedbackSummary,
  IntelligenceBriefing,
  NoteRevision,
  WikiIndex,
  WikiCatalogPage,
  WikiPagePayload,
  WikiRelationType,
  WorkspaceShellSummary,
} from "./types";
import type { StagedTakeoutCandidate } from "./takeout";
import { DEFAULT_CONTRIBUTION_INSTRUCTIONS } from "./agent-config";
import { safeExternalUrl } from "./utils";

class ApiError extends Error {
  status: number;
  details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

export type HumanRole = "owner" | "admin" | "editor" | "viewer";

export interface Principal {
  user: { id: string; name: string; email: string };
  team: { id: string; name: string };
  roles: string[];
  role: HumanRole;
  csrfToken: string;
}

export interface RealtimeToken {
  endpoint: string;
  projectId: string;
  databaseId: string;
  jwt: string;
  expiresAt: string;
}

export interface PublicHealth {
  status: string;
  service: string;
  version: string;
  release?: string;
  revision?: string;
  persistence?: "local" | "appwrite";
}

export interface LatticeHealth extends PublicHealth {
  backend?: string;
  database?: { status: string };
  workspace?: { status: string };
  migration?: { status: string };
  vault?: { status: string; path?: string; noteCount?: number };
  packArtifacts?: { status: string };
}

let csrfToken: string | null = null;

function normalizeNote(raw: any): Note {
  const originSource = raw.origin?.source ?? raw.originSource;
  return {
    ...raw,
    content: raw.content ?? raw.snippet ?? raw.excerpt ?? "",
    summary: raw.summary ?? raw.snippet ?? raw.excerpt ?? "",
    sourceUrl: safeExternalUrl(raw.sourceUrl ?? raw.source?.url) ?? null,
    tags: raw.tags ?? [],
    taxonomy: raw.taxonomy ?? [],
    subcategories: raw.subcategories ?? [],
    origin: originSource ? {
      source: originSource,
      date: raw.origin?.date ?? raw.originDate,
      conversationId: raw.origin?.conversationId ?? raw.originConversationId,
      conversationTitle: raw.origin?.conversationTitle ?? raw.originConversationTitle,
    } : undefined,
    createdAt: raw.createdAt ?? new Date(0).toISOString(),
    updatedAt: raw.updatedAt ?? raw.createdAt ?? new Date(0).toISOString(),
  } as Note;
}

function normalizePack(raw: any): ContextPack {
  return {
    ...raw,
    version: raw.version ?? raw.currentVersion ?? 0,
    noteIds: raw.noteIds ?? raw.config?.noteIds ?? [],
    tags: raw.tags ?? raw.config?.tags ?? [],
    query: raw.query ?? raw.config?.query ?? null,
    excludeNoteIds: raw.excludeNoteIds ?? raw.config?.excludeNoteIds ?? [],
    maxDepth: raw.maxDepth ?? raw.relationDepth ?? raw.config?.relationDepth ?? 0,
    budgetPolicy: raw.budgetPolicy ?? raw.config?.budgetPolicy ?? null,
    estimatedTokens: raw.estimatedTokens ?? raw.tokenEstimate ?? 0,
    agentIds: raw.agentIds ?? [],
  } as ContextPack;
}

function normalizeAgent(raw: any): Agent {
  return {
    ...raw,
    permissions: raw.permissions ?? raw.scopes ?? [],
    disabled: raw.disabled ?? Boolean(raw.revokedAt),
    packIds: raw.packIds ?? [],
    requestCount: raw.requestCount ?? raw.requests ?? 0,
    tokenCount: raw.tokenCount ?? raw.estimatedTokens ?? raw.tokens ?? 0,
  } as Agent;
}

function normalizeAgentEnrollment(raw: any): AgentEnrollment {
  return {
    ...raw,
    permissions: raw.permissions ?? raw.scopes ?? [],
    packIds: raw.packIds ?? [],
    contributionInstructions: raw.contributionInstructions ?? DEFAULT_CONTRIBUTION_INSTRUCTIONS,
  } as AgentEnrollment;
}

function normalizePublicAgentEnrollment(raw: any): PublicAgentEnrollment {
  return {
    ...raw,
    permissions: raw.permissions ?? raw.scopes ?? [],
    packs: raw.packs ?? [],
    contributionInstructions: raw.contributionInstructions ?? DEFAULT_CONTRIBUTION_INSTRUCTIONS,
  } as PublicAgentEnrollment;
}

function normalizeActivity(raw: any): ActivityReceipt {
  return {
    ...raw,
    agentName: raw.agentName ?? raw.agent?.name ?? raw.agentId ?? null,
    packName: raw.packName ?? raw.pack?.name ?? null,
    noteIds: raw.noteIds ?? [],
    tokens: raw.tokens ?? raw.tokenEstimate ?? 0,
    status: raw.status ?? raw.outcome ?? "ok",
  } as ActivityReceipt;
}

function normalizeManifest(raw: any) {
  const values = Array.isArray(raw) ? raw : raw?.notes ?? [];
  return values.map((item: any) => ({
    ...item,
    slug: item.slug ?? item.path?.replace(/\.md$/i, "") ?? item.id,
    checksum: item.checksum ?? item.contentHash,
  }));
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const mutating = !["GET", "HEAD", "OPTIONS"].includes(method);
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(mutating && csrfToken ? { "X-Lattice-CSRF": csrfToken } : {}),
      ...init?.headers,
    },
  });

  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!response.ok) {
    const message =
      parsed && typeof parsed === "object" && "error" in parsed
        ? typeof (parsed as { error: unknown }).error === "object" && (parsed as any).error?.message
          ? String((parsed as any).error.message)
          : String((parsed as { error: unknown }).error)
        : `Request failed (${response.status})`;
    throw new ApiError(message, response.status, parsed);
  }

  if (parsed && typeof parsed === "object" && "data" in parsed) {
    return (parsed as { data: T }).data;
  }
  return parsed as T;
}

function queryString(values: Record<string, string | number | boolean | undefined>) {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== "") params.set(key, String(value));
  });
  const result = params.toString();
  return result ? `?${result}` : "";
}

function packWire(payload: Partial<ContextPack>) {
  return {
    name: payload.name,
    purpose: payload.purpose ?? payload.description,
    mode: payload.mode,
    tokenBudget: payload.tokenBudget,
    budgetPolicy: payload.budgetPolicy,
    noteIds: payload.noteIds,
    tags: payload.tags,
    query: payload.query,
    excludeNoteIds: payload.excludeNoteIds,
    relationDepth: payload.maxDepth,
  };
}

export const api = {
  runtime: () => request<{ authRequired: boolean; persistence: "local" | "appwrite" }>("/api/runtime"),
  workspaceShell: (signal?: AbortSignal) => request<WorkspaceShellSummary>("/api/workspace-shell", { signal }),
  me: () =>
    request<Principal>("/api/auth/me").then((principal) => {
      csrfToken = principal.csrfToken;
      return principal;
    }),
  login: (email: string, password: string) =>
    request<Principal>("/api/auth/login", {
      method: "POST",
      headers: { "X-Lattice-CSRF": "1" },
      body: JSON.stringify({ email, password }),
    }).then((principal) => {
      csrfToken = principal.csrfToken;
      return principal;
    }),
  logout: async () => {
    await request<unknown>("/api/auth/logout", { method: "POST" });
    csrfToken = null;
  },
  realtimeToken: (signal?: AbortSignal) =>
    request<RealtimeToken>("/api/auth/realtime-token", {
      method: "POST",
      body: "{}",
      signal,
    }),
  publicHealth: (signal?: AbortSignal) => request<PublicHealth>("/api/health", { signal }),
  publicReady: (signal?: AbortSignal) => request<PublicHealth>("/api/ready", { signal }),
  health: () => request<LatticeHealth>("/api/status"),
  overview: async () => {
    const dashboard = await request<any>("/api/dashboard?days=30");
    const summary = dashboard.summary ?? {};
    const requestByDate = new Map<string, number>(
      (dashboard.requestSeries ?? []).map((point: any) => [point.date, point.requests ?? point.value ?? point.count ?? 0]),
    );
    const tokenByDate = new Map<string, number>(
      (dashboard.tokenSeries ?? []).map((point: any) => [point.date, point.tokens ?? point.value ?? point.count ?? 0]),
    );
    const dates = Array.from(new Set([...requestByDate.keys(), ...tokenByDate.keys()])).sort();
    return {
      notes: summary.notes ?? summary.noteCount ?? summary.totalNotes ?? 0,
      links: summary.links ?? summary.linkCount ?? summary.totalLinks ?? 0,
      packs: summary.packs ?? summary.contextPacks ?? summary.packCount ?? summary.totalContextPacks ?? 0,
      agents: summary.agents ?? summary.activeAgents ?? 0,
      requests: summary.requests ?? summary.requestCount ?? 0,
      tokens: summary.tokens ?? summary.tokenCount ?? summary.estimatedTokens ?? 0,
      orphans: summary.orphanCount ?? summary.orphans ?? dashboard.orphans?.length ?? 0,
      staleSources: summary.staleSourceCount ?? summary.staleSources ?? dashboard.staleSources?.length ?? 0,
      proposals: summary.pendingProposals ?? summary.writeProposals ?? summary.proposals ?? 0,
      usage: dates.map((date) => ({ date, requests: requestByDate.get(date) ?? 0, tokens: tokenByDate.get(date) ?? 0 })),
      taxonomy: dashboard.taxonomyUsage ?? [],
      recentActivity: dashboard.recentActivity ?? [],
      mostUsed: (dashboard.topNotes ?? []).map((note: any) => ({
        id: note.id,
        title: note.title,
        type: note.type,
        count: note.count ?? note.usageCount ?? 0,
      })),
      memory: {
        hitRate: summary.hitRate ?? 0,
        errorRate: summary.errorRate ?? 0,
        p95LatencyMs: summary.p95LatencyMs ?? 0,
        p95Tokens: summary.p95Tokens ?? 0,
        contextBuilds: summary.contextBuilds ?? 0,
        retrievalCoverageRate: summary.retrievalCoverageRate ?? 0,
        linkedNoteRate: summary.linkedNoteRate ?? 0,
        classifiedNoteRate: summary.classifiedNoteRate ?? 0,
        unusedNoteCount: summary.unusedNoteCount ?? 0,
        unresolvedLinkCount: summary.unresolvedLinkCount ?? 0,
      },
    } satisfies Overview;
  },
  search: (
    query: string,
    filters: {
      type?: string;
      tag?: string;
      classificationKind?: "tag" | "taxonomy";
      status?: string;
      limit?: number;
    } = {},
    options: { signal?: AbortSignal } = {},
  ) =>
    request<{ items: Note[]; total: number; truncated?: boolean }>(
      `/api/search${queryString({ q: query, ...filters })}`,
      { signal: options.signal },
    ).then((result) => ({
      ...result,
      items: result.items.map(normalizeNote),
    })),
  buildContext: (payload: {
    objective: string;
    tokenBudget?: number;
    maxNotes?: number;
    seedNoteIds?: string[];
    tags?: string[];
    relationDepth?: number;
    packIds?: string[];
  }) => request<SmartContextResult>("/api/context/build", { method: "POST", body: JSON.stringify(payload) }),
  notes: (filters: {
    query?: string;
    type?: string;
    status?: string;
    tag?: string;
    classificationKind?: "tag" | "taxonomy";
    limit?: number;
    offset?: number;
  } = {}) =>
    request<{ items: Note[] }>(`/api/notes${queryString(filters)}`).then((result) => result.items.map(normalizeNote)),
  notesPage: (
    filters: {
      query?: string;
      type?: string;
      status?: string;
      tag?: string;
      classificationKind?: "tag" | "taxonomy";
      limit?: number;
      offset?: number;
    } = {},
    options: { signal?: AbortSignal } = {},
  ) => request<{ items: Note[]; total: number; totalIsLowerBound?: boolean }>(
    `/api/notes${queryString(filters)}`,
    { signal: options.signal },
  ).then((result) => ({ ...result, items: result.items.map(normalizeNote) })),
  curationNotes: async () => {
    const notes: Note[] = [];
    const pageSize = 250;
    const maximum = 2_000;
    for (let offset = 0; offset < maximum; offset += pageSize) {
      const result = await request<{ items: Note[]; total: number }>(`/api/notes${queryString({ limit: pageSize, offset })}`);
      notes.push(...result.items.map(normalizeNote));
      if (notes.length >= result.total || result.items.length < pageSize) break;
    }
    return notes;
  },
  note: (id: string) =>
    request<{ note: any; backlinks: any[]; outlinks: any[] }>(`/api/notes/${encodeURIComponent(id)}`).then((result) => ({
      ...normalizeNote(result.note),
      backlinks: result.backlinks.map((link) => ({
        id: link.fromNoteId,
        slug: link.fromNoteId,
        title: link.fromTitle,
        type: "note",
      })),
      outgoingLinks: result.outlinks.filter((link) => link.toNoteId).map((link) => ({
        id: link.toNoteId,
        slug: link.toNoteId,
        title: link.toTitle ?? link.target,
        type: "note",
      })),
    })),
  noteRevision: (noteId: string, revisionId: string) =>
    request<NoteRevision>(`/api/notes/${encodeURIComponent(noteId)}/revisions/${encodeURIComponent(revisionId)}`)
      .then((revision) => ({ ...revision, note: normalizeNote(revision.note) })),
  wiki: (filters: { q?: string; category?: string; limit?: number; offset?: number } = {}, options: { signal?: AbortSignal } = {}) =>
    request<WikiIndex>(`/api/wiki${queryString(filters)}`, { signal: options.signal }).then((result) => ({
      ...result,
      items: result.items.map(normalizeNote),
      overlaps: result.overlaps.map((overlap) => ({
        ...overlap,
        canonical: normalizeNote(overlap.canonical),
        candidates: overlap.candidates.map(normalizeNote),
      })),
    })),
  wikiCatalogPage: (filters: { limit?: number; offset?: number } = {}, options: { signal?: AbortSignal } = {}) =>
    request<WikiCatalogPage>(`/api/wiki/catalog${queryString(filters)}`, { signal: options.signal }).then((result) => ({
      ...result,
      items: result.items.map(normalizeNote),
    })),
  wikiPage: (id: string, options: { signal?: AbortSignal } = {}) =>
    request<any>(`/api/wiki/pages/${encodeURIComponent(id)}`, { signal: options.signal }).then((result): WikiPagePayload => ({
      ...result,
      note: normalizeNote(result.note),
      relations: (result.relations ?? []).map((item: any) => ({ ...item, note: normalizeNote(item.note) })),
      replacedPages: (result.replacedPages ?? []).map(normalizeNote),
      suggestedRelations: (result.suggestedRelations ?? []).map((item: any) => ({ ...item, note: normalizeNote(item.note) })),
      graph: {
        nodes: (result.graph?.nodes ?? []).map((node: any) => ({ ...node, title: node.title ?? node.label })),
        links: (result.graph?.edges ?? result.graph?.links ?? []).map((edge: any) => ({ ...edge, type: edge.type ?? edge.kind })),
        total: result.graph?.total,
        truncated: result.graph?.truncated,
        totalIsLowerBound: result.graph?.totalIsLowerBound,
      },
    })),
  addWikiRelation: (sourceId: string, targetId: string, type: WikiRelationType, label?: string) =>
    request<Note>(`/api/wiki/pages/${encodeURIComponent(sourceId)}/relations`, { method: "POST", body: JSON.stringify({ targetId, type, label }) }).then(normalizeNote),
  removeWikiRelation: (sourceId: string, targetId: string, type: WikiRelationType) =>
    request<Note>(`/api/wiki/pages/${encodeURIComponent(sourceId)}/relations`, { method: "DELETE", body: JSON.stringify({ targetId, type }) }).then(normalizeNote),
  consolidateWiki: (canonicalId: string, duplicateIds: string[]) =>
    request<{ canonical: Note; archived: Array<{ id: string; title: string }> }>("/api/wiki/consolidate", { method: "POST", body: JSON.stringify({ canonicalId, duplicateIds }) })
      .then((result) => ({ ...result, canonical: normalizeNote(result.canonical) })),
  createNote: (note: Partial<Note>) =>
    request<Note>("/api/notes", {
      method: "POST",
      body: JSON.stringify({
        ...note,
        ...(note.sourceUrl ? { source: { url: note.sourceUrl } } : {}),
        originSource: note.origin?.source,
        originDate: note.origin?.date,
        originConversationId: note.origin?.conversationId,
        originConversationTitle: note.origin?.conversationTitle,
        origin: undefined,
        sourceUrl: undefined,
        summary: undefined,
      }),
    }).then(normalizeNote),
  identifyKnowledge: (note: Partial<Note>) =>
    request<import("./types").AutomaticIdentification>("/api/identification/preview", {
      method: "POST",
      body: JSON.stringify({
        title: note.title,
        content: note.content ?? "",
        type: note.type,
        tags: note.tags,
        taxonomy: note.taxonomy,
        category: note.category,
        subcategories: note.subcategories,
        description: note.description,
        properties: note.properties,
        ...(note.sourceUrl ? { source: { url: note.sourceUrl } } : {}),
        originSource: note.origin?.source,
        originDate: note.origin?.date,
        originConversationId: note.origin?.conversationId,
        originConversationTitle: note.origin?.conversationTitle,
      }),
    }),
  backfillAutomaticIdentification: (limit = 25) =>
    request<import("./types").AutomaticIdentificationBackfillResult>("/api/identification/backfill", {
      method: "POST",
      body: JSON.stringify({ limit }),
    }),
  updateNote: (id: string, note: Partial<Note>) =>
    request<Note>(`/api/notes/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({
        ...note,
        ...(note.sourceUrl ? { source: { url: note.sourceUrl } } : {}),
        originSource: note.origin?.source,
        originDate: note.origin?.date,
        originConversationId: note.origin?.conversationId,
        originConversationTitle: note.origin?.conversationTitle,
        origin: undefined,
        sourceUrl: undefined,
        summary: undefined,
        status: undefined,
      }),
    }).then(normalizeNote),
  archiveNote: (id: string) =>
    request<Note>(`/api/notes/${encodeURIComponent(id)}`, { method: "DELETE" }).then(normalizeNote),
  graph: async (options: { taxonomy?: boolean; focus?: string; depth?: number; limit?: number } = {}) => {
    const result = await request<{ nodes: any[]; edges?: any[]; links?: any[]; total?: number; truncated?: boolean; totalIsLowerBound?: boolean }>(`/api/graph${queryString(options)}`);
    return {
      nodes: result.nodes.map((node) => ({ ...node, title: node.title ?? node.label, degree: node.degree ?? node.usageCount })),
      links: (result.edges ?? result.links ?? []).map((edge) => ({ ...edge, type: edge.type ?? edge.kind })),
      total: result.total,
      truncated: result.truncated,
      totalIsLowerBound: result.totalIsLowerBound,
    } as GraphPayload;
  },
  taxonomy: () => request<{ items: TaxonomyItem[] }>("/api/taxonomy").then((result) => result.items),
  capture: (payload: { url: string; tags?: string[]; fallbackTitle?: string; fallbackContent?: string }) =>
    request<Note>("/api/capture", { method: "POST", body: JSON.stringify(payload) }).then(normalizeNote),
  packs: () => request<{ items: ContextPack[] }>("/api/context-packs").then((result) => result.items.map(normalizePack)),
  pack: (id: string) =>
    request<{ pack: ContextPack }>(`/api/context-packs/${encodeURIComponent(id)}`).then((result) => normalizePack(result.pack)),
  savePack: (payload: Partial<ContextPack>) =>
    payload.id
      ? request<{ pack: ContextPack }>(`/api/context-packs/${encodeURIComponent(payload.id)}`, {
          method: "PATCH",
          body: JSON.stringify(packWire(payload)),
        }).then((result) => normalizePack(result.pack))
      : request<{ pack: ContextPack }>("/api/context-packs", { method: "POST", body: JSON.stringify(packWire(payload)) }).then(
          (result) => normalizePack(result.pack),
        ),
  previewPack: async (payload: Partial<ContextPack>) => {
    const result = await request<any>("/api/context-packs/preview", {
      method: "POST",
      body: JSON.stringify({ id: payload.id, ...packWire(payload) }),
    });
    return {
      ...result,
      payload: result.payloadMarkdown,
      estimatedTokens: result.tokenEstimate,
      tokenBudget: result.manifest?.tokenBudget ?? result.tokenBudget ?? payload.tokenBudget ?? 0,
      budgetDecision: result.manifest?.budgetDecision ?? result.budgetDecision,
      omittedNoteIds: result.manifest?.omittedNoteIds ?? result.omittedNoteIds,
      version: result.manifest?.packVersion ?? null,
      manifest: normalizeManifest(result.manifest),
    } as PackPreview;
  },
  setPackAssignments: (id: string, agentIds: string[]) =>
    request<{ pack: ContextPack }>(`/api/context-packs/${encodeURIComponent(id)}/assignments`, {
      method: "PUT",
      body: JSON.stringify({ agentIds }),
    }).then((result) => normalizePack(result.pack)),
  contextPackRequests: (status?: string) =>
    request<{ items: ContextPackRequest[]; total: number }>(`/api/context-pack-requests${status ? `?status=${encodeURIComponent(status)}` : ""}`),
  reviewContextPackRequest: (id: string, decision: "approved" | "rejected", autoCreatePack = true) =>
    request<ContextPackRequest>(`/api/context-pack-requests/${encodeURIComponent(id)}/review`, {
      method: "POST",
      body: JSON.stringify({ decision, autoCreatePack }),
    }),
  retrievePack: async (id: string) => {
    const result = await request<any>(`/api/context-packs/${encodeURIComponent(id)}/context`);
    return {
      ...result,
      payload: result.payloadMarkdown,
      estimatedTokens: result.tokenEstimate,
      tokenBudget: result.manifest?.tokenBudget ?? result.tokenBudget ?? 0,
      budgetDecision: result.manifest?.budgetDecision ?? result.budgetDecision,
      omittedNoteIds: result.manifest?.omittedNoteIds ?? result.omittedNoteIds,
      version: result.version ?? result.packVersion ?? result.manifest?.packVersion,
      manifest: normalizeManifest(result.manifest),
    } as PackPreview;
  },
  agents: () => request<{ items: Agent[] }>("/api/agents").then((result) => result.items.map(normalizeAgent)),
  createAgent: (payload: { name: string; description?: string; permissions: string[]; packIds: string[] }) =>
    request<{ agent: Agent; token: string }>("/api/agents", {
      method: "POST",
      body: JSON.stringify({ name: payload.name, description: payload.description, scopes: payload.permissions, packIds: payload.packIds }),
    }).then((result) => ({ ...normalizeAgent(result.agent), token: result.token })),
  updateAgent: (id: string, payload: Partial<Agent>) =>
    request<Agent>(`/api/agents/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ ...payload, scopes: payload.permissions }),
    }).then(normalizeAgent),
  rotateAgentToken: (id: string) =>
    request<{ agent: Agent; token: string }>(`/api/agents/${encodeURIComponent(id)}/rotate`, { method: "POST" }).then(
      (result) => ({ ...normalizeAgent(result.agent), token: result.token }),
    ),
  deleteAgent: (id: string) =>
    request<void>(`/api/agents/${encodeURIComponent(id)}`, { method: "DELETE" }),
  agentEnrollments: () =>
    request<{ items: AgentEnrollment[] }>("/api/agent-enrollments").then((result) =>
      result.items.map(normalizeAgentEnrollment),
    ),
  createAgentEnrollment: (payload: {
    label: string;
    permissions: string[];
    packIds: string[];
    contributionInstructions: string;
    expiresInHours: number;
  }) =>
    request<{ enrollment: AgentEnrollment; inviteUrl: string }>("/api/agent-enrollments", {
      method: "POST",
      body: JSON.stringify({
        label: payload.label,
        scopes: payload.permissions,
        packIds: payload.packIds,
        contributionInstructions: payload.contributionInstructions,
        expiresInHours: payload.expiresInHours,
      }),
    }).then((result) => ({ ...result, enrollment: normalizeAgentEnrollment(result.enrollment) })),
  approveAgentEnrollment: (id: string) =>
    request<{ enrollment: AgentEnrollment; agent: Agent }>(
      `/api/agent-enrollments/${encodeURIComponent(id)}/approve`,
      { method: "POST" },
    ).then((result) => ({
      enrollment: normalizeAgentEnrollment(result.enrollment),
      agent: normalizeAgent(result.agent),
    })),
  rejectAgentEnrollment: (id: string) =>
    request<AgentEnrollment>(`/api/agent-enrollments/${encodeURIComponent(id)}/reject`, {
      method: "POST",
    }).then(normalizeAgentEnrollment),
  revokeAgentEnrollment: (id: string) =>
    request<AgentEnrollment>(`/api/agent-enrollments/${encodeURIComponent(id)}/revoke`, {
      method: "POST",
    }).then(normalizeAgentEnrollment),
  publicAgentEnrollment: (inviteToken: string) =>
    request<PublicAgentEnrollment>("/api/enrollment/agent", {
      headers: { "X-Lattice-Invite": inviteToken },
    }).then(normalizePublicAgentEnrollment),
  claimAgentEnrollment: (
    inviteToken: string,
    payload: { name: string; description?: string; credentialHash: string; credentialPrefix: string },
  ) =>
    request<PublicAgentEnrollment>("/api/enrollment/agent/claim", {
      method: "POST",
      headers: { "X-Lattice-Invite": inviteToken },
      body: JSON.stringify(payload),
    }).then(normalizePublicAgentEnrollment),
  activityPage: (options: { limit?: number; offset?: number; action?: string; agentId?: string } = {}) => {
    const params = new URLSearchParams();
    params.set("limit", String(options.limit ?? 100));
    if (options.offset) params.set("offset", String(options.offset));
    if (options.action) params.set("action", options.action);
    if (options.agentId) params.set("agentId", options.agentId);
    return request<{ items: ActivityReceipt[]; total: number }>(`/api/activity?${params}`).then((result) => ({
      ...result,
      items: result.items.map(normalizeActivity),
    }));
  },
  activity: (limit = 100) =>
    request<{ items: ActivityReceipt[] }>(`/api/activity?limit=${limit}`).then((result) => result.items.map(normalizeActivity)),
  proposals: (status?: Proposal["status"]) => request<{ items: Proposal[] }>(
    `/api/proposals${status ? `?status=${encodeURIComponent(status)}` : ""}`,
  ).then((result) => result.items),
  knowledgeIntegrity: (force = false) => request<import("./types").KnowledgeIntegrityAudit>(
    force ? "/api/knowledge-integrity/run" : "/api/knowledge-integrity",
    force ? { method: "POST" } : {},
  ),
  analyseProposal: (id: string) => request<Proposal>(`/api/proposals/${encodeURIComponent(id)}/analyse`, { method: "POST" }),
  runProposalAutonomy: (limit = 100) => request<{ scanned: number; applied: number; escalated: number; errors: number }>(
    "/api/proposals/autonomy/run",
    { method: "POST", body: JSON.stringify({ limit }) },
  ),
  reviewProposal: (
    id: string,
    decision: "accept" | "reject",
    overlapResolution?: import("./proposals").ProposalOverlapResolution,
    sectionResolutions?: import("./proposals").ProposalSectionResolution[],
  ) =>
    request<Proposal | { proposal: Proposal; note: Note; notes?: Note[] }>(
      `/api/proposals/${encodeURIComponent(id)}/${decision}`,
      {
        method: "POST",
        ...((decision === "accept" && overlapResolution) || sectionResolutions
          ? { body: JSON.stringify({ overlapResolution, sectionResolutions }) }
          : {}),
      },
    ),
  takeoutImports: () => request<{ items: TakeoutImport[] }>("/api/takeout-imports").then((result) => result.items),
  createTakeoutImport: (payload: {
    filename: string;
    fileHash: string;
    parserVersion: string;
    conversationCount: number;
    highSignalCount: number;
    redactionCount: number;
  }) => request<TakeoutImport>("/api/takeout-imports", { method: "POST", body: JSON.stringify(payload) }),
  addTakeoutCandidates: (id: string, candidates: StagedTakeoutCandidate[]) =>
    request<{ items: TakeoutCandidate[]; added: number }>(`/api/takeout-imports/${encodeURIComponent(id)}/candidates`, {
      method: "POST",
      body: JSON.stringify({ candidates }),
    }),
  finalizeTakeoutImport: (id: string) =>
    request<TakeoutImport>(`/api/takeout-imports/${encodeURIComponent(id)}/finalize`, { method: "POST" }),
  takeoutCandidates: (
    id: string,
    options: { status?: "pending" | "accepted" | "rejected"; limit?: number; offset?: number } = {},
    requestOptions: { signal?: AbortSignal } = {},
  ) => request<TakeoutCandidatePage>(
    `/api/takeout-imports/${encodeURIComponent(id)}/candidates${queryString(options)}`,
    { signal: requestOptions.signal },
  ),
  takeoutReviewCandidates: (
    options: {
      status?: "pending" | "accepted" | "rejected";
      recommendation?: "recommended" | "review" | "all";
      q?: string;
      kind?: string;
      tag?: string;
      taxonomy?: string;
      year?: string;
      limit?: number;
      offset?: number;
    } = {},
    requestOptions: { signal?: AbortSignal } = {},
  ) => request<TakeoutReviewPage>(
    `/api/takeout-review/candidates${queryString(options)}`,
    { signal: requestOptions.signal },
  ),
  updateTakeoutCandidate: (
    id: string,
    payload: Partial<Pick<TakeoutCandidate, "kind" | "title" | "content" | "tags" | "taxonomy">>,
  ) => request<TakeoutCandidate>(`/api/takeout-candidates/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  }),
  takeoutCandidate: (id: string, requestOptions: { signal?: AbortSignal } = {}) =>
    request<TakeoutCandidate>(`/api/takeout-candidates/${encodeURIComponent(id)}`, {
      signal: requestOptions.signal,
    }),
  bulkAcceptTakeoutCandidates: (id: string, candidateIds: string[]) =>
    request<BulkTakeoutAcceptance>(`/api/takeout-imports/${encodeURIComponent(id)}/candidates/bulk-accept`, {
      method: "POST",
      body: JSON.stringify({ candidateIds }),
    }),
  acceptTakeoutCandidate: (id: string, payload: Partial<Pick<TakeoutCandidate, "title" | "content" | "tags" | "taxonomy">> & { type?: string }) =>
    request<{ candidate: TakeoutCandidate; note: Note }>(`/api/takeout-candidates/${encodeURIComponent(id)}/accept`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  rejectTakeoutCandidate: (id: string) =>
    request<TakeoutCandidate>(`/api/takeout-candidates/${encodeURIComponent(id)}/reject`, { method: "POST" }),
  missions: () => request<{ items: Mission[]; total: number; truncated: boolean }>("/api/missions"),
  createMission: (payload: {
    title: string;
    objective: string;
    summary?: string;
    status?: Mission["status"];
    dueAt?: string | null;
    packIds?: string[];
    agentIds?: string[];
    noteIds?: string[];
    tags?: string[];
    taxonomy?: string[];
  }) => request<Mission>("/api/missions", { method: "POST", body: JSON.stringify(payload) }),
  updateMission: (id: string, payload: Partial<Pick<Mission, "title" | "objective" | "summary" | "status" | "dueAt" | "owner" | "packIds" | "agentIds" | "noteIds" | "tags" | "taxonomy">>) =>
    request<Mission>(`/api/missions/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(payload) }),
  missionContext: (id: string, payload: Partial<{ objective: string; tokenBudget: number; maxNotes: number; seedNoteIds: string[]; tags: string[]; relationDepth: number; packIds: string[] }> = {}) =>
    request<SmartContextResult>(`/api/missions/${encodeURIComponent(id)}/context`, { method: "POST", body: JSON.stringify(payload) }),
  skills: () => request<{ items: LivingSkill[]; total: number; truncated: boolean }>("/api/skills"),
  createSkill: (payload: {
    title: string;
    description?: string;
    instructions: string;
    enabled?: boolean;
    sourceNoteId?: string | null;
    packIds?: string[];
    tags?: string[];
    taxonomy?: string[];
  }) => request<LivingSkill>("/api/skills", { method: "POST", body: JSON.stringify(payload) }),
  updateSkill: (id: string, payload: Partial<LivingSkill>) =>
    request<LivingSkill>(`/api/skills/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(payload) }),
  decisionRadar: () => request<DecisionRadar>("/api/decision-radar"),
  memoryChanges: (since: string, limit = 100) =>
    request<MemoryChanges>(`/api/memory/changes${queryString({ since, limit })}`).then((result) => ({
      ...result,
      items: result.items.map(normalizeNote),
    })),
  dossiers: () => request<{ items: DossierSummary[]; scannedNotes: number; truncated: boolean }>("/api/dossiers"),
  dossier: (kind: DossierSummary["kind"], key: string) =>
    request<Dossier>(`/api/dossiers/${kind}/${encodeURIComponent(key)}`).then((result) => ({
      ...result,
      notes: result.notes.map(normalizeNote),
      decisions: result.decisions.map(normalizeNote),
      sources: result.sources.map(normalizeNote),
    })),
  outcomes: (days = 30) => request<OutcomeSummary>(`/api/outcomes${queryString({ days })}`),
  releaseFeedback: (version: string, requestOptions: { signal?: AbortSignal } = {}) =>
    request<ReleaseFeedbackSummary>(`/api/release-feedback${queryString({ version })}`, { signal: requestOptions.signal }),
  submitReleaseFeedback: (payload: { version: string; rating: ReleaseFeedbackRating; priority: boolean; comment?: string }) =>
    request<ReleaseFeedbackSummary>("/api/release-feedback", { method: "POST", body: JSON.stringify(payload) }),
  submitOutcome: (payload: { receiptId: string; rating: "helpful" | "mixed" | "unhelpful"; noteIds?: string[]; comment?: string; missingKnowledge?: string }) =>
    request<ActivityReceipt>("/api/outcomes", { method: "POST", body: JSON.stringify(payload) }).then(normalizeActivity),
  automations: () => request<{ items: MemoryAutomation[]; total: number; truncated: boolean }>("/api/automations"),
  createAutomation: (payload: {
    title: string;
    description?: string;
    enabled?: boolean;
    trigger: MemoryAutomation["trigger"];
    action: MemoryAutomation["action"];
    tags?: string[];
    taxonomy?: string[];
  }) => request<MemoryAutomation>("/api/automations", { method: "POST", body: JSON.stringify(payload) }),
  updateAutomation: (id: string, payload: Partial<MemoryAutomation>) =>
    request<MemoryAutomation>(`/api/automations/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(payload) }),
  runAutomation: (id: string, force = true) =>
    request<{ automation: MemoryAutomation; ran: boolean; result: unknown }>(`/api/automations/${encodeURIComponent(id)}/run`, { method: "POST", body: JSON.stringify({ force }) }),
  briefing: () => request<IntelligenceBriefing>("/api/briefing/today").then((result) => ({
    ...result,
    changes: { ...result.changes, items: result.changes.items.map(normalizeNote) },
    outcomes: { ...result.outcomes, recent: result.outcomes.recent.map(normalizeActivity) },
  })),
  syncVault: () => request<{ scanned: number; created: number; updated: number; archived: number; links: number }>("/api/vault/sync", { method: "POST" }),
  exportUrl: "/api/export/json",
};

export { ApiError };
